import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, forwardRef, Directive, HostListener, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@angular/cdk/portal';
import { PortalModule } from '@angular/cdk/portal';
import * as i3 from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import * as i1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BehaviorSubject } from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * contants to create timer with HOURS or MINUTES
 */
const HOURS = ['12', '11', '1', '10', '2', '9', '3', '8', '4', '7', '5', '6'];
const HOURS24 = ['00', '23', '13', '22', '14', '21', '15', '20', '16', '19', '17', '18'];
const MINUTES = [
    '00',
    '55',
    '05',
    '50',
    '10',
    '45',
    '15',
    '40',
    '20',
    '35',
    '25',
    '30',
];

class MccTimerPickerComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        /**
         * Receive selected _hour after confirm
         */
        this._selectedHour = '12';
        /**
         * Receive selected _minute after confirm
         */
        this._selectedMinute = '00';
        /**
         * Receive selected _period after confirm
         */
        this._selectedPeriod = 'am';
        this._clock = new BehaviorSubject(HOURS);
        this._focus = 'hour';
        this._hour = '12';
        this._minute = '00';
        this._period = 'am';
        this._hideButtons = false;
        /**
         * Format of the hour to be emited on confirm
         */
        this.format = '12';
        this.min = '00:00 am';
        this.max = '12:00 pm';
        /**
         * Change btnCancel label
         */
        this.btnCancel = 'Cancel';
        /**
         * Change btnConfirm label
         */
        this.btnConfirm = 'Confirm';
        /**
         * Event emited when confirm button is pressed.
         * If buttons are hidden, the event is emited when value is changed
         */
        this.selected = new EventEmitter();
        /**
         * Set to true when timer picker have been connected with another component
         */
        this.connected = false;
    }
    /**
     * Current value (hour/minute) to create the clock
     */
    get clock$() {
        return this._clock.asObservable();
    }
    /**
     * Return hours in 24h format
     */
    get hours24() {
        return HOURS24;
    }
    /**
     * Type there is in focus (hour/minute)
     */
    get focus() {
        return this._focus;
    }
    set focus(value) {
        if (value !== this._focus) {
            this._focus = value;
            this._clock.next(this._focus === 'hour' ? HOURS : MINUTES);
        }
    }
    /**
     * State of the overlay
     */
    get isOpen() {
        return this._isOpen;
    }
    set isOpen(value) {
        this._isOpen = coerceBooleanProperty(value);
        this.changeDetectorRef.detectChanges();
    }
    /**
     * Return temporary selected hour (const HOURS)
     */
    get hour() {
        return this._hour;
    }
    /**
     * Checks if hour has only one digit
     */
    get needsSpacer() {
        return parseInt(this._hour, 10) < 10;
    }
    /**
     * Return temporary selected minute (const MINUTES)
     */
    get minute() {
        return this._minute;
    }
    /**
     * Return temporary selected period (am/pm)
     */
    get period() {
        return this._period;
    }
    /**
     * Hide Confirm and Cancel buttons
     */
    set hideButtons(value) {
        this._hideButtons = coerceBooleanProperty(value);
    }
    get hideButtons() {
        return this._hideButtons;
    }
    /**
     * Return timer option class to create line between the middle of the clock and
     * the option
     */
    getSelectedClass() {
        let name = 'selected-index-';
        if (this.focus === 'hour') {
            // convert to number
            const hour = parseInt(this.hour, 10);
            // check if hour is mid night or afternoon in 24h format
            if (hour === 0 || hour > 12) {
                name += HOURS24.findIndex(h => h === this.hour);
                name += ' hour-format-24';
            }
            else {
                name += HOURS.findIndex(h => h === this.hour);
            }
        }
        else {
            name += MINUTES.findIndex(m => m === this.minute);
        }
        return name;
    }
    /**
     * Select option from the clock.
     */
    select(value) {
        if (this.focus === 'hour') {
            this._hour = value;
            this.focus = 'min';
        }
        else {
            this._minute = value;
            // if buttons are hidden, emit new event when value is changed
            if (this._hideButtons) {
                this.confirmSelectedTime();
            }
        }
    }
    /**
     * Returns array containing time, hour and period fragments from time string
     */
    parseTimeInput(time) {
        const parsed = time.split(/\s|:/).map((fragment, index) => {
            return index === 2 ? fragment : parseInt(fragment, 10);
        });
        if (parsed.length === 2) {
            // assume we are using 24 hour time format
            const hours = parsed[0];
            if (hours > 11) {
                parsed[0] = hours - 12;
                parsed.push('pm');
            }
            else {
                parsed.push('am');
            }
        }
        return parsed;
    }
    /**
     * Returns true if option value is not valid
     */
    isOptionDisabled(value) {
        const [minHour, minMinutes, minPeriod] = this.parseTimeInput(this.min);
        const [maxHour, maxMinutes, maxPeriod] = this.parseTimeInput(this.max);
        const optionValue = parseInt(value, 10);
        const selectedHour = parseInt(this._hour, 10);
        const selectedPeriod = this._period;
        if (this.focus === 'hour') {
            if (optionValue < minHour && selectedPeriod === minPeriod) {
                return true;
            }
            else if (optionValue > maxHour && selectedPeriod === maxPeriod) {
                return true;
            }
        }
        else {
            if (selectedHour === minHour && selectedPeriod === minPeriod && optionValue < minMinutes) {
                return true;
            }
            else if (selectedHour === maxHour && selectedPeriod === maxPeriod && optionValue > maxMinutes) {
                return true;
            }
        }
        return false;
    }
    /**
     * Change period of the clock
     */
    changePeriod(period) {
        this._period = period;
        // if buttons are hidden, emit new event when value is changed
    }
    /**
     * Update selected color, close the panel and notify the user
     */
    backdropClick() {
        if (this._hideButtons) {
            this.confirmSelectedTime();
        }
        else {
            this.cancelSelection();
        }
    }
    /**
     * Change values to last confirm select time
     */
    cancelSelection() {
        this._hour = this._selectedHour;
        this._minute = this._selectedMinute;
        this._period = this._selectedPeriod;
        this.isOpen = false;
    }
    /**
     * Set new values of time and emit new event with the formatted timer
     */
    confirmSelectedTime() {
        this._selectedHour = this.hour;
        this._selectedMinute = this.minute;
        this._selectedPeriod = this.period;
        // format string to emit selected time
        let formated;
        if (this.format === '12') {
            formated = `${this.hour}:${this.minute} ${this.period}`;
        }
        else {
            let hour = this.hour;
            if (this.period === 'pm') {
                hour = `${parseInt(hour, 10) + 12}`;
            }
            formated = `${hour}:${this.minute}`;
        }
        this.selected.emit(formated);
        this.isOpen = false;
    }
}
MccTimerPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
MccTimerPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.0.0", type: MccTimerPickerComponent, selector: "mcc-timer-picker", inputs: { hideButtons: "hideButtons", format: ["mccTimerPickerFormat", "format"], min: ["mccTimerPickerMin", "min"], max: ["mccTimerPickerMax", "max"], btnCancel: "btnCancel", btnConfirm: "btnConfirm" }, outputs: { selected: "selected" }, ngImport: i0, template: "<!-- show component inside overlay -->\n<ng-container *ngIf=\"connected\">\n  <ng-template cdkConnectedOverlay cdkConnectedOverlayHasBackdrop cdkConnectedOverlayBackdropClass=\"mcc-timer-picker-backdrop\" [cdkConnectedOverlayOrigin]=\"trigger\"\n    [cdkConnectedOverlayOpen]=\"isOpen\" (backdropClick)=\"backdropClick()\">\n\n    <ng-template [cdkPortalOutlet]=\"panelComponent\"></ng-template>\n\n  </ng-template>\n</ng-container>\n\n<ng-template *ngIf=\"!connected\" [cdkPortalOutlet]=\"panelComponent\"></ng-template>\n\n<ng-template cdkPortal #panelComponent=\"cdkPortal\">\n  <div class=\"mcc-timer-picker-overlay mat-elevation-z4\" role=\"dialog\" aria-label=\"Timer picker\">\n\n    <div class=\"mcc-timer-picker-header mat-primary\">\n      <div class=\"mcc-timer-picker-header-clock\">\n        <button mat-icon-button class=\"mcc-timer-picker-hours\" [ngClass]=\"{ 'mcc-active': focus === 'hour' }\"\n                (click)=\"focus = 'hour'\">\n         <span><span *ngIf=\"needsSpacer\">&nbsp;&nbsp;</span>{{ hour }}</span>\n        </button>\n        <span class=\"mcc-timer-picker-separator\">:</span>\n        <button mat-icon-button class=\"mcc-timer-picker-minutes\" [ngClass]=\"{ 'mcc-active': focus === 'min' }\"\n                (click)=\"focus = 'min'\">\n          <span>{{ minute }}</span>\n        </button>\n\n        <div class=\"mcc-timer-picker-am-pm\" *ngIf=\"format === '12'\">\n          <button mat-icon-button [ngClass]=\"{ 'mcc-active': period === 'am' }\" (click)=\"changePeriod('am')\">\n            AM\n          </button>\n          <button mat-icon-button [ngClass]=\"{ 'mcc-active': period === 'pm' }\" (click)=\"changePeriod('pm')\">\n            PM\n          </button>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"mcc-timer-picker-content\">\n\n      <div class=\"mcc-timer-picker-clock\">\n        <div class=\"mcc-timer-picker-dot\"></div>\n        <div class=\"mcc-timer-picker-line\" [ngClass]=\"getSelectedClass()\"></div>\n        <ng-container *ngFor=\"let option of clock$ | async;let i = index;\">\n          <button mat-icon-button class=\"mcc-timer-picker-option\" [ngClass]=\"{\n          'mcc-timer-picker-option-selected': focus === 'hour' && hour == option || focus === 'min' && minute === option }\"\n            [id]=\"'option-'+i\" (click)=\"select(option)\" [disabled]=\"isOptionDisabled(option)\">\n            {{ option }}\n          </button>\n\n        </ng-container>\n\n        <ng-container *ngIf=\"format === '24' && focus === 'hour'\">\n          <ng-container *ngFor=\"let option of hours24; let i = index;\">\n            <button mat-icon-button class=\"mcc-timer-picker-option hours\" [ngClass]=\"{\n              'mcc-timer-picker-option-selected': hour == option}\"\n                    [id]=\"'option-hour-'+i\" (click)=\"select(option)\" [disabled]=\"isOptionDisabled(option)\">\n              {{ option }}\n            </button>\n          </ng-container>\n        </ng-container>\n      </div>\n\n    </div>\n\n    <div *ngIf=\"!hideButtons\" class=\"mcc-timer-picker-actions\">\n      <div style=\"flex: 1 auto;\"></div>\n      <button mat-button role=\"button\" aria-label=\"Cancel\" (click)=\"cancelSelection()\">\n        {{ btnCancel }}\n      </button>\n      <button mat-button role=\"button\" aria-label=\"Confirm\" (click)=\"confirmSelectedTime()\">\n        {{ btnConfirm }}\n      </button>\n    </div>\n\n  </div>\n</ng-template>\n", styles: [".mcc-timer-picker-overlay{font-family:\"Roboto\",sans-serif;width:290px;border-radius:4px;background-color:#fff}.mcc-timer-picker-header{display:flex;justify-content:center;width:100%;padding:4px 0;box-sizing:border-box;border-top-left-radius:4px;border-top-right-radius:4px;background:#00796b}.mcc-timer-picker-header .mcc-timer-picker-header-clock{display:flex;align-items:center}.mcc-timer-picker-hours,.mcc-timer-picker-minutes{width:63px;height:63px;font-size:50px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-hours.mcc-standalone,.mcc-timer-picker-minutes.mcc-standalone{text-align:right}.mcc-timer-picker-hours.mcc-active,.mcc-timer-picker-minutes.mcc-active{color:#fff}.mcc-timer-picker-24hours{width:63px;height:63px;font-size:50px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-24hours.mcc-standalone{text-align:right}.mcc-timer-picker-24hours.mcc-active{color:#fff}.mcc-timer-picker-separator{font-size:43px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-am-pm{display:flex;flex-direction:column}.mcc-timer-picker-am-pm button{width:25px;height:25px;line-height:25px;font-size:12px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-am-pm button.mcc-active{color:#fff}.mcc-timer-picker-content{width:inherit;height:260px;padding:15px 0 5px}.mcc-timer-picker-clock{position:relative;width:250px;height:250px;margin:0 auto;background-color:#eee;border-radius:50%;text-align:center}.mcc-timer-picker-clock .mcc-timer-picker-dot{position:absolute;width:10px;height:10px;border-radius:50%;background-color:#00796b;top:120px;left:120px}.mcc-timer-picker-clock .mcc-timer-picker-line{position:absolute;width:2px;height:100px;top:10%;left:124px;transform-origin:50% 100%;background-color:#00796b}.mcc-timer-picker-clock .mcc-timer-picker-line.hour-format-24{height:75px;top:20%}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-0{transform:rotate(0)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-1{transform:rotate(327deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-2{transform:rotate(30deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-3{transform:rotate(300deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-4{transform:rotate(60deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-5{transform:rotate(270deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-6{transform:rotate(90deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-7{transform:rotate(240deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-8{transform:rotate(120deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-9{transform:rotate(210deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-10{transform:rotate(150deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-11{transform:rotate(180deg)}.mcc-timer-picker-clock .mcc-timer-picker-option{position:absolute}.mcc-timer-picker-clock .mcc-timer-picker-option.hours.mat-icon-button{width:26px;height:26px;line-height:10px;font-size:70%}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-0{top:45px;left:112px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-1{top:57px;left:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-2{top:57px;right:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-3{top:81px;left:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-4{top:81px;right:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-5{top:112px;left:45px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-6{top:112px;right:45px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-7{bottom:81px;left:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-8{bottom:81px;right:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-9{bottom:57px;left:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-10{bottom:57px;right:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-11{bottom:45px;left:112px}.mcc-timer-picker-clock .mcc-timer-picker-option.mcc-timer-picker-option-selected{color:#fff;background-color:#00796b}.mcc-timer-picker-clock .mcc-timer-picker-option#option-0{top:0;left:105px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-1{top:15px;left:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-2{top:16px;right:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-3{top:52px;left:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-4{top:52px;right:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-5{top:105px;left:0}.mcc-timer-picker-clock .mcc-timer-picker-option#option-6{top:105px;right:0}.mcc-timer-picker-clock .mcc-timer-picker-option#option-7{bottom:52px;left:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-8{bottom:52px;right:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-9{bottom:16px;left:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-10{bottom:16px;right:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-11{bottom:0;left:105px}.mcc-timer-picker-actions{display:flex;padding:4px}.mcc-timer-picker-actions button{text-transform:uppercase}mat-icon-button{width:30px;height:30px;line-height:30px}\n"], components: [{ type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.CdkConnectedOverlay, selector: "[cdk-connected-overlay], [connected-overlay], [cdkConnectedOverlay]", inputs: ["cdkConnectedOverlayOrigin", "cdkConnectedOverlayPositions", "cdkConnectedOverlayPositionStrategy", "cdkConnectedOverlayOffsetX", "cdkConnectedOverlayOffsetY", "cdkConnectedOverlayWidth", "cdkConnectedOverlayHeight", "cdkConnectedOverlayMinWidth", "cdkConnectedOverlayMinHeight", "cdkConnectedOverlayBackdropClass", "cdkConnectedOverlayPanelClass", "cdkConnectedOverlayViewportMargin", "cdkConnectedOverlayScrollStrategy", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayTransformOriginOn", "cdkConnectedOverlayHasBackdrop", "cdkConnectedOverlayLockPosition", "cdkConnectedOverlayFlexibleDimensions", "cdkConnectedOverlayGrowAfterOpen", "cdkConnectedOverlayPush"], outputs: ["backdropClick", "positionChange", "attach", "detach", "overlayKeydown", "overlayOutsideClick"], exportAs: ["cdkConnectedOverlay"] }, { type: i4.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { type: i4.CdkPortal, selector: "[cdkPortal]", exportAs: ["cdkPortal"] }, { type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "async": i2.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mcc-timer-picker', preserveWhitespaces: false, changeDetection: ChangeDetectionStrategy.OnPush, template: "<!-- show component inside overlay -->\n<ng-container *ngIf=\"connected\">\n  <ng-template cdkConnectedOverlay cdkConnectedOverlayHasBackdrop cdkConnectedOverlayBackdropClass=\"mcc-timer-picker-backdrop\" [cdkConnectedOverlayOrigin]=\"trigger\"\n    [cdkConnectedOverlayOpen]=\"isOpen\" (backdropClick)=\"backdropClick()\">\n\n    <ng-template [cdkPortalOutlet]=\"panelComponent\"></ng-template>\n\n  </ng-template>\n</ng-container>\n\n<ng-template *ngIf=\"!connected\" [cdkPortalOutlet]=\"panelComponent\"></ng-template>\n\n<ng-template cdkPortal #panelComponent=\"cdkPortal\">\n  <div class=\"mcc-timer-picker-overlay mat-elevation-z4\" role=\"dialog\" aria-label=\"Timer picker\">\n\n    <div class=\"mcc-timer-picker-header mat-primary\">\n      <div class=\"mcc-timer-picker-header-clock\">\n        <button mat-icon-button class=\"mcc-timer-picker-hours\" [ngClass]=\"{ 'mcc-active': focus === 'hour' }\"\n                (click)=\"focus = 'hour'\">\n         <span><span *ngIf=\"needsSpacer\">&nbsp;&nbsp;</span>{{ hour }}</span>\n        </button>\n        <span class=\"mcc-timer-picker-separator\">:</span>\n        <button mat-icon-button class=\"mcc-timer-picker-minutes\" [ngClass]=\"{ 'mcc-active': focus === 'min' }\"\n                (click)=\"focus = 'min'\">\n          <span>{{ minute }}</span>\n        </button>\n\n        <div class=\"mcc-timer-picker-am-pm\" *ngIf=\"format === '12'\">\n          <button mat-icon-button [ngClass]=\"{ 'mcc-active': period === 'am' }\" (click)=\"changePeriod('am')\">\n            AM\n          </button>\n          <button mat-icon-button [ngClass]=\"{ 'mcc-active': period === 'pm' }\" (click)=\"changePeriod('pm')\">\n            PM\n          </button>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"mcc-timer-picker-content\">\n\n      <div class=\"mcc-timer-picker-clock\">\n        <div class=\"mcc-timer-picker-dot\"></div>\n        <div class=\"mcc-timer-picker-line\" [ngClass]=\"getSelectedClass()\"></div>\n        <ng-container *ngFor=\"let option of clock$ | async;let i = index;\">\n          <button mat-icon-button class=\"mcc-timer-picker-option\" [ngClass]=\"{\n          'mcc-timer-picker-option-selected': focus === 'hour' && hour == option || focus === 'min' && minute === option }\"\n            [id]=\"'option-'+i\" (click)=\"select(option)\" [disabled]=\"isOptionDisabled(option)\">\n            {{ option }}\n          </button>\n\n        </ng-container>\n\n        <ng-container *ngIf=\"format === '24' && focus === 'hour'\">\n          <ng-container *ngFor=\"let option of hours24; let i = index;\">\n            <button mat-icon-button class=\"mcc-timer-picker-option hours\" [ngClass]=\"{\n              'mcc-timer-picker-option-selected': hour == option}\"\n                    [id]=\"'option-hour-'+i\" (click)=\"select(option)\" [disabled]=\"isOptionDisabled(option)\">\n              {{ option }}\n            </button>\n          </ng-container>\n        </ng-container>\n      </div>\n\n    </div>\n\n    <div *ngIf=\"!hideButtons\" class=\"mcc-timer-picker-actions\">\n      <div style=\"flex: 1 auto;\"></div>\n      <button mat-button role=\"button\" aria-label=\"Cancel\" (click)=\"cancelSelection()\">\n        {{ btnCancel }}\n      </button>\n      <button mat-button role=\"button\" aria-label=\"Confirm\" (click)=\"confirmSelectedTime()\">\n        {{ btnConfirm }}\n      </button>\n    </div>\n\n  </div>\n</ng-template>\n", styles: [".mcc-timer-picker-overlay{font-family:\"Roboto\",sans-serif;width:290px;border-radius:4px;background-color:#fff}.mcc-timer-picker-header{display:flex;justify-content:center;width:100%;padding:4px 0;box-sizing:border-box;border-top-left-radius:4px;border-top-right-radius:4px;background:#00796b}.mcc-timer-picker-header .mcc-timer-picker-header-clock{display:flex;align-items:center}.mcc-timer-picker-hours,.mcc-timer-picker-minutes{width:63px;height:63px;font-size:50px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-hours.mcc-standalone,.mcc-timer-picker-minutes.mcc-standalone{text-align:right}.mcc-timer-picker-hours.mcc-active,.mcc-timer-picker-minutes.mcc-active{color:#fff}.mcc-timer-picker-24hours{width:63px;height:63px;font-size:50px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-24hours.mcc-standalone{text-align:right}.mcc-timer-picker-24hours.mcc-active{color:#fff}.mcc-timer-picker-separator{font-size:43px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-am-pm{display:flex;flex-direction:column}.mcc-timer-picker-am-pm button{width:25px;height:25px;line-height:25px;font-size:12px;font-weight:400;color:#ffffff4d}.mcc-timer-picker-am-pm button.mcc-active{color:#fff}.mcc-timer-picker-content{width:inherit;height:260px;padding:15px 0 5px}.mcc-timer-picker-clock{position:relative;width:250px;height:250px;margin:0 auto;background-color:#eee;border-radius:50%;text-align:center}.mcc-timer-picker-clock .mcc-timer-picker-dot{position:absolute;width:10px;height:10px;border-radius:50%;background-color:#00796b;top:120px;left:120px}.mcc-timer-picker-clock .mcc-timer-picker-line{position:absolute;width:2px;height:100px;top:10%;left:124px;transform-origin:50% 100%;background-color:#00796b}.mcc-timer-picker-clock .mcc-timer-picker-line.hour-format-24{height:75px;top:20%}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-0{transform:rotate(0)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-1{transform:rotate(327deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-2{transform:rotate(30deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-3{transform:rotate(300deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-4{transform:rotate(60deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-5{transform:rotate(270deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-6{transform:rotate(90deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-7{transform:rotate(240deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-8{transform:rotate(120deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-9{transform:rotate(210deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-10{transform:rotate(150deg)}.mcc-timer-picker-clock .mcc-timer-picker-line.selected-index-11{transform:rotate(180deg)}.mcc-timer-picker-clock .mcc-timer-picker-option{position:absolute}.mcc-timer-picker-clock .mcc-timer-picker-option.hours.mat-icon-button{width:26px;height:26px;line-height:10px;font-size:70%}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-0{top:45px;left:112px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-1{top:57px;left:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-2{top:57px;right:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-3{top:81px;left:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-4{top:81px;right:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-5{top:112px;left:45px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-6{top:112px;right:45px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-7{bottom:81px;left:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-8{bottom:81px;right:57px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-9{bottom:57px;left:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-10{bottom:57px;right:80px}.mcc-timer-picker-clock .mcc-timer-picker-option.hours#option-hour-11{bottom:45px;left:112px}.mcc-timer-picker-clock .mcc-timer-picker-option.mcc-timer-picker-option-selected{color:#fff;background-color:#00796b}.mcc-timer-picker-clock .mcc-timer-picker-option#option-0{top:0;left:105px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-1{top:15px;left:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-2{top:16px;right:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-3{top:52px;left:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-4{top:52px;right:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-5{top:105px;left:0}.mcc-timer-picker-clock .mcc-timer-picker-option#option-6{top:105px;right:0}.mcc-timer-picker-clock .mcc-timer-picker-option#option-7{bottom:52px;left:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-8{bottom:52px;right:16px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-9{bottom:16px;left:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-10{bottom:16px;right:52px}.mcc-timer-picker-clock .mcc-timer-picker-option#option-11{bottom:0;left:105px}.mcc-timer-picker-actions{display:flex;padding:4px}.mcc-timer-picker-actions button{text-transform:uppercase}mat-icon-button{width:30px;height:30px;line-height:30px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { hideButtons: [{
                type: Input
            }], format: [{
                type: Input,
                args: ['mccTimerPickerFormat']
            }], min: [{
                type: Input,
                args: ['mccTimerPickerMin']
            }], max: [{
                type: Input,
                args: ['mccTimerPickerMax']
            }], btnCancel: [{
                type: Input
            }], btnConfirm: [{
                type: Input
            }], selected: [{
                type: Output
            }] } });

class MccTimerPickerOriginDirective {
    /**
     * Reference to the element on which the directive is applied.
     */
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        /**
         * Emit changes from the origin
         */
        this.change = new BehaviorSubject('');
        /**
         * Emit changes from the origin
         */
        this.hasFocus = new EventEmitter();
    }
    onFocus() {
        this.hasFocus.emit();
    }
    /**
     * This method will be called by the forms API to write to the view when
     * programmatic (model -> view) changes are requested.
     */
    writeValue(time) {
        this.renderer.setProperty(this.elementRef.nativeElement, 'value', time);
        this.change.next(time);
        if (this.propagateChanges) {
            this.propagateChanges(time);
        }
    }
    /**
     * This method will be called by the time picker
     */
    writeValueFromTimerPicker(time) {
        this.renderer.setProperty(this.elementRef.nativeElement, 'value', time);
        this.propagateChanges(time);
    }
    /**
     * This method will be called from origin whe key is up
     */
    writeValueFromKeyup(time) {
        this.change.next(time);
        this.propagateChanges(time);
    }
    /**
     * This is called by the forms API on initialization so it can update the
     * form model when values propagate from the view (view -> model).
     * @param fn any
     */
    registerOnChange(fn) {
        this.propagateChanges = fn;
    }
    /**
     * This is called by the forms API on initialization so it can update the form model on blur
     * @param fn any
     */
    registerOnTouched(fn) { }
    /**
     * called by the forms API when the control status changes to or from "DISABLED"
     * @param isDisabled boolean
     */
    setDisabledState(isDisabled) {
        this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    }
}
MccTimerPickerOriginDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerOriginDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
MccTimerPickerOriginDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.0", type: MccTimerPickerOriginDirective, selector: "[mccTimerPickerOrigin], [mcc-timer-picker-origin]", outputs: { change: "change", hasFocus: "hasFocus" }, host: { listeners: { "focus": "onFocus()" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MccTimerPickerOriginDirective),
            multi: true,
        },
    ], exportAs: ["mccTimerPickerOrigin"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerOriginDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mccTimerPickerOrigin], [mcc-timer-picker-origin]',
                    exportAs: 'mccTimerPickerOrigin',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => MccTimerPickerOriginDirective),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { change: [{
                type: Output
            }], hasFocus: [{
                type: Output
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }] } });
class MccConnectedTimerPickerDirective {
    constructor(timerPicker, changeDetectorRef) {
        this.timerPicker = timerPicker;
        this.changeDetectorRef = changeDetectorRef;
        this.timerPicker.connected = true;
    }
    ngAfterViewInit() {
        if (!this._timerPickerSub) {
            this.timerPicker.trigger = this.origin;
            this._attachTimerPicker();
        }
    }
    ngOnDestroy() {
        if (this._originFocus && !this._originFocus.closed) {
            this._originFocus.unsubscribe();
        }
        if (this._timerPickerSub && !this._timerPickerSub.closed) {
            this._timerPickerSub.unsubscribe();
        }
    }
    /**
     * Attach the timer picker to origin element (input)
     */
    _attachTimerPicker() {
        this._originFocus = this.origin.hasFocus.subscribe(() => {
            this.timerPicker.focus = 'hour';
            this.timerPicker.isOpen = true;
            this.changeDetectorRef.detectChanges();
        });
        this._timerPickerSub = this.timerPicker.selected.subscribe(value => this.origin.writeValueFromTimerPicker(value));
    }
}
MccConnectedTimerPickerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccConnectedTimerPickerDirective, deps: [{ token: MccTimerPickerComponent }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
MccConnectedTimerPickerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.0", type: MccConnectedTimerPickerDirective, selector: "[mccConnectedTimerPicker], [mcc-connected-timer-picker]", inputs: { origin: ["mccConnectedTimerPickerOrigin", "origin"] }, exportAs: ["mccConnectedTimerPicker"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccConnectedTimerPickerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[mccConnectedTimerPicker], [mcc-connected-timer-picker]',
                    exportAs: 'mccConnectedTimerPicker',
                }]
        }], ctorParameters: function () { return [{ type: MccTimerPickerComponent }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { origin: [{
                type: Input,
                args: ['mccConnectedTimerPickerOrigin']
            }] } });

class MccTimerPickerModule {
}
MccTimerPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MccTimerPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerModule, declarations: [MccTimerPickerComponent,
        MccTimerPickerOriginDirective,
        MccConnectedTimerPickerDirective], imports: [CommonModule, PortalModule, OverlayModule, MatButtonModule], exports: [MccTimerPickerComponent,
        MccTimerPickerOriginDirective,
        MccConnectedTimerPickerDirective] });
MccTimerPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerModule, imports: [[CommonModule, PortalModule, OverlayModule, MatButtonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: MccTimerPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule, OverlayModule, MatButtonModule],
                    declarations: [
                        MccTimerPickerComponent,
                        MccTimerPickerOriginDirective,
                        MccConnectedTimerPickerDirective,
                    ],
                    exports: [
                        MccTimerPickerComponent,
                        MccTimerPickerOriginDirective,
                        MccConnectedTimerPickerDirective,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { HOURS, HOURS24, MINUTES, MccConnectedTimerPickerDirective, MccTimerPickerComponent, MccTimerPickerModule, MccTimerPickerOriginDirective };
